<template>
  <modal name="pex-card" :clickToClose="false">
    <div class="content">
      <!-- TODO: get Spanish translations for hardcoded strings -->
      <h1 class="text-xl mb-4 font-bold" :class="[!companion ? 'light-blue' : 'green']">Connect PEX Card for Rewards</h1>
      <div class="card-image">
        <img src="../../assets/images/pex-card.png" />
      </div>
      <p>
        The patient was enrolled in a rewards program to incentivize positive behaviors. 
        Rewards are delivered to a PEX debit card. Link a PEX card to the patient by entering the 
        last four digits of the card number below.
      </p>
      <div class="status-message red-text mt-4" v-if="error">{{error}}</div>
      <div class="form-group">
        <div class="card-number" v-if="!noCard && !availableCards.length">
          <input id="digit_1" class="number" @keydown="numberChanged($event)" :disabled="loading || !adding" v-model="firstDigit" maxlength="1" />
          <input id="digit_2" class="number" @keydown="numberChanged($event)" :disabled="loading || !adding" v-model="secondDigit" maxlength="1" />
          <input id="digit_3" class="number" @keydown="numberChanged($event)" :disabled="loading || !adding" v-model="thirdDigit" maxlength="1" />
          <input id="digit_4" class="number" @keydown="numberChanged($event)" :disabled="loading || !adding" v-model="fourthDigit" maxlength="1" />
        </div>
        <div v-if="!noCard && availableCards.length">
          <p>Multiple cards were found with a matching last four digits of <strong>{{last4Digits}}</strong>. Please choose the correct card to assign:</p>
          <div class="available-cards">
            <LabeledCheckbox v-for="card of availableCards" :key="card.Name" :labelValue="card.Name" :checkValue="card.selected" 
              @update="updateSelectedCard(card)" :disabled="loading || !adding" />
          </div>
        </div>
        <p v-if="noCard">PEX cards can be linked to patients later via Patient Details.</p>
      </div>
      <div class="button-holder" v-if="!loading">
        <div class="status-message green-text" v-if="!adding">Success! Give card to patient.</div>
        <LabeledCheckbox class="labeled-checkbox" v-if="adding" :labelValue="'No card available'" 
          :checkValue="noCard" @update="noCard = !noCard; error = null;" />
        <button class="w-24" :class="last4Digits.length != 4 && !noCard ? 'disabled' : 'enabled'"
          :disabled="last4Digits.length != 4 && !noCard" @click="closeModal()">
            {{noCard || !adding ? 'Close' : 'Connect'}}
          </button>
      </div>
      <p class="disclaimer" v-if="!adding && !loading">The card is active. The patient does <strong><u>not</u></strong> need to activate the card. Any activation sticker may be removed.</p>
      <div class="loading" v-if="loading">
        <LoadingUI />
      </div>
    </div>
  </modal>
</template>

<script>
import LoadingUI from '../LoadingUI.vue'
import LabeledCheckbox from '../LabeledCheckbox.vue'
import { translations } from '../../translations'
import { createCard } from '../../services/contingency-management-service'

export default {
  name: 'PEXCardModal',
  components: { // imported components
    LoadingUI,
    LabeledCheckbox
  },
  props: { // props passed into component
    patient: Object,
    companion: Boolean
  },
  data() { // component props
    return {
      language: translations(),
      name: '',
      cardFirstName: '',
      last4Digits: '',
      firstDigit: '',
      secondDigit: '',
      thirdDigit: '',
      fourthDigit: '',
      adding: true,
      loading: false,
      noCard : false,
      error: null,
      availableCards: []
    }
  },
  created() { /* Called synchronously after the instance is created */ },
  mounted() { /* Called after the instance has been mounted */ },
  methods: { // component functions
    numberChanged(event) {
      const input = event.target.getAttribute('id');
      const digit = parseInt(input.split('_')[1]);
      // check all number key codes
      if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105)) {
        // assign the value
        switch (digit) {
          case 1:
            this.firstDigit = event.key;
            break;
          case 2:
            this.secondDigit = event.key;
            break;
          case 3:
            this.thirdDigit = event.key;
            break;
          case 4:
            this.fourthDigit = event.key;
            break;
        }
        event.preventDefault();
        // move to the next input
        const next_input = document.getElementById(`digit_${digit + 1}`);
        if (next_input) {
          next_input.focus();
        }
      } else if (event.keyCode == 8) { // check if backspace was pressed
        // remove the value
        switch (digit) {
          case 1:
            this.firstDigit = '';
            break;
          case 2:
            this.secondDigit = '';
            break;
          case 3:
            this.thirdDigit = '';
            break;
          case 4:
            this.fourthDigit = '';
            break;
        }
        event.preventDefault();
        // move to the previous input
        const previous_input = document.getElementById(`digit_${digit - 1}`);
        if (previous_input) {
          previous_input.focus();
        }
      } else { // do nothing for all other keys
        event.preventDefault();
      }
      // assemble the full number
      this.last4Digits = `${this.firstDigit}${this.secondDigit}${this.thirdDigit}${this.fourthDigit}`;
    },
    updateSelectedCard(card) {
      this.cardFirstName = card.Name;  // include full name since full name is queried on the backend
      for (let card of this.availableCards) card.selected = false;
      card.selected = true;
      this.$forceUpdate();
    },
    closeModal() {
      // check if we need to add a card
      if (this.adding && !this.noCard) {
        this.loading = true;
        this.error = null;
        createCard(this.patient.sso_id, this.last4Digits, this.patient.enrollment_ids, this.cardFirstName).then(res => {
          if (res.success) {
            this.loading = false;
            // if data is an array, multiple cards with the last 4 digits were found
            if (res.data && res.data.length) {
              this.availableCards = res.data;
            } else {
              this.adding = false;
            }
          } else {
            this.errorCallback(res);
          }
        }).catch(err => {
          this.errorCallback(err);
        })
      } else {
        this.$emit('close-modal');
        this.$modal.hide('pex-card');
      }
    },
    errorCallback(err) {
      this.loading = false;
      this.error = err.message;
      console.log(err.message);
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .content {
    text-align: left;
    width: 500px;
  }
  h1 {
    text-align: center;
  }
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  button {
    height: 40px;
    font-size: 14px;
    color: #fff;
    border: none;
    padding: 6px 12px;
    border-radius: 40px;
    background: #750097;
  }
  .compbutton {
  background: #2c6759;
}
  .light-blue {
    color: #750097;
  }
  .green{
    color:#2c6759;
  }
  label {
    display: block;
    font-size: 13px;
    text-align: left;
    margin-bottom: 8px;
    color: #597495;
    font-weight: 700;
  }
  label.required::after {
    content: " *";
    color: red;
  }
  input {
    font-size: 14px;
    font-weight: 400;
    color: #333;
    height: 40px;
    width: 100%;
    border-radius: 4px;
    padding: 6px 12px;
    line-height: 1.42857143;
    border: 1px solid #ccc;
    -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
    -webkit-transition: border-color ease-in-out .15s,-webkit-box-shadow ease-in-out .15s;
    -o-transition: border-color ease-in-out .15s,box-shadow ease-in-out .15s;
    transition: border-color ease-in-out .15s,box-shadow ease-in-out .15s;
  }
    input:focus {
      border-color: #66afe9;
      outline: 0;
      -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
    }
    input:disabled {
      color: #a0a0a0;
    }
  .form-group {
    margin: 30px 0 45px;
  }
  .card-number {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px 0 30px;
  }
  .available-cards {
    display: flex;
    flex-direction: column;
    margin: 0 15px;
  }
  .number {
    font-size: 48px;
    margin: 0 15px;
    height: 88px;
    width: 60px;
    text-align: center;
    color: #333;
  }
  p {
    margin-top: 15px;
    padding: 0 15px;
  }
  .card-image {
    width: calc(100% / 3);
    margin: auto;
  }
    .card-image img {
      width: 100%;
    }
  .status-message {
    text-align: center;
    font-weight: 600;
    margin-left: auto;
    margin-right: auto;
  }
  .disclaimer {
    margin: 30px 0 0;
    padding: 0 15px;
  }
  .red-text {
    color: red;
  }
  .green-text {
    color: #31CE6D;
  }
  .button-holder {
    display: flex;
    align-items: center;
    margin-top: 45px;
  }
  .labeled-checkbox {
    margin: auto;
  }
  button {
    height: 40px;
    font-size: 14px;
    color: #750097;
    border: 1px solid #750097;
    padding: 6px 12px;
    border-radius: 40px;
    background: #fff;
    margin: auto;
    min-width: 112px;
  }
  button.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  button.enabled {
    color: #fff;
    background: #750097;
  }
</style>

<!-- Not "scoped" to style modal -->
<style>
  .vm--container {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .vm--overlay {
    background: rgba(8, 17, 53, 0.38) !important;
  }
  .vm--modal {
    text-align: center;
    border-radius: 4px !important;
    background: #ffffff;
    padding: 20px;
    height: auto !important;
    width: auto !important;
    max-width: 600px;
    top: auto !important;
    left: auto !important;
  }
</style>
